import type { HTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { t, useT, type Messages } from '../i18n';

/** A spotlight's id; its texts live in the dictionary under spotlights.cards. */
export type SpotlightId = keyof Messages['spotlights']['cards'];

/** What a card shows: `label` names it in filter chips, the card itself
    renders `title` (serif heading) and `description` (body text). */
export type SpotlightText = Messages['spotlights']['cards'][SpotlightId];

export interface Spotlight {
  id: SpotlightId;
  filterType: 'kapitel' | 'subExpType' | 'expenditureType' | 'prefix' | 'hauptgruppe' | 'kapitelScope';
  // A data key (a German category name, a chapter, a prefix) — never translated.
  filterValue: string;
}

/** A spotlight's texts in the widget language (i18n de.ts / en.ts). */
export function spotlightText(spotlight: Spotlight): SpotlightText {
  return t().spotlights.cards[spotlight.id];
}

// Pre-configured spotlight filters. Adding a spotlight means an entry here plus
// its texts under spotlights.cards in both dictionaries (the SpotlightId type
// enforces it). The parent's handleSpotlightClick switch matches on
// filterType, and the active detection uses the same field.
export const SPOTLIGHTS: Spotlight[] = [
  {
    id: 'kernhaushalt',
    // Excludes every chapter whose name starts with "Anlage" — the same
    // chapter set the Sondervermögen dropdown offers, inverted. Resolved
    // server-side (buildWhereClause) because the rule reads kapitel_text,
    // which no client-side filter option carries.
    filterType: 'kapitelScope',
    filterValue: 'kernhaushalt',
  },
  {
    id: 'klima-fonds',
    filterType: 'kapitel',
    filterValue: '60|92',
  },
  {
    id: 'svik',
    filterType: 'kapitel',
    filterValue: '60|93',
  },
  {
    id: 'bereichsausnahmen',
    filterType: 'prefix',
    filterValue: 'B',
  },
  {
    id: 'investitionen',
    filterType: 'hauptgruppe',
    filterValue: '7,8',
  },
  {
    id: 'subventionen-unternehmen',
    filterType: 'expenditureType',
    filterValue: 'Subventionen an Unternehmen',
  },
  {
    id: 'oeffentliche-leistungen',
    filterType: 'expenditureType',
    filterValue: 'Öffentliche Leistungen',
  },
  {
    id: 'finanzierungskosten',
    filterType: 'expenditureType',
    filterValue: 'Finanzierungskosten',
  },
  {
    id: 'bildung',
    // Plain sub_expenditure_type value (no "Parent|Sub"): "Bildung" exists under
    // two parents — Öffentliche Leistungen and Subventionen an Haushalte — and
    // the server's plain-subExpType branch matches both at once.
    filterType: 'subExpType',
    filterValue: 'Bildung',
  },
];

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

// Section caption — Figma anchors the "Spotlights" word at 16 px UI(S),
// default text colour, regular weight. Not the prior small-caps muted style.
const SectionTitle = styled.h3`
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: 400;
  color: var(--color-text-default);
  line-height: 1.2;
  margin: 0;
`;

// Horizontal rail of spotlight cards. Figma frame allows overflow-x scroll.
// The native scrollbar is hidden across all engines — cards visibly cut off
// at the right edge signal that more content is reachable. Users still
// scroll via wheel/trackpad/touch.
const ScrollRail = styled.div`
  display: flex;
  gap: var(--space-8);
  overflow-x: auto;
  /* overflow-x: auto hard-clips along BOTH axes at the scroll viewport edges,
     which is what slices the soft card shadow into a hard line. The Figma
     "Tile Shadow" is -2px 10px 30px, so it reaches ~20px above / ~40px below
     / ~32px to each side of a card. Pad the rail by at least those amounts so
     the shadow lands inside the clip box and breathes, then cancel the
     horizontal pad with an equal negative margin so the cards stay flush with
     the section. (Vertical pad just adds height — no margin needed there.) */
  padding: var(--space-7) var(--space-8) var(--space-14);
  margin: 0 calc(-1 * var(--space-8));
  scroll-snap-type: x proximity;
  /* Snap aligns cards to the scrollport edge, which the negative margin moved
     space-8 LEFT of the section edge — without this, the initial snap drags
     the rail to scrollLeft = padding-left and the first card paints cut off
     at the widget boundary. Match the snap origin to the padded content edge. */
  scroll-padding-inline: var(--space-8);

  /* Hide the scrollbar visually on every engine. */
  scrollbar-width: none;        /* Firefox */
  -ms-overflow-style: none;     /* IE / legacy Edge */
  &::-webkit-scrollbar {        /* Chrome / Safari / modern Edge */
    display: none;
  }
`;

// Spotlight card. Fixed 15rem width (vs Figma's 276px) so the rail of cards
// reads as accents rather than dominating the viewport above the chart;
// height follows content, with title and description pushed apart.
const SpotlightCardBase = styled.div<{ active: boolean }>`
  flex: 0 0 auto;
  /* 18.46rem ≈ 240px at the host's 13px root — the card's original design width.
     (Plain 15rem lands at 195px on a 13px root, shrinking the card ~19%.) */
  width: 18.46rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-5) var(--space-6) var(--space-4);
  background-color: var(--color-bg-surface);
  border-radius: var(--radius-m);
  /* Figma "Tile Shadow" — a soft, large-blur drop shadow offset slightly down
     and left. Inlined rather than using --shadow-m so it matches the template
     exactly. The rail's padding (above) is sized to keep this from clipping. */
  box-shadow: -2px 10px 30px 0 rgba(0, 0, 0, 0.15);
  border: 2px solid ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  scroll-snap-align: start;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: -2px 14px 38px 0 rgba(0, 0, 0, 0.2);
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

// GT Alpina, 1.85rem ≈ 24px at the host's 13px root — the intended title size,
// between shared serif-m (20px) and serif-l (30px). (Plain 1.5rem renders 19.5px
// on a 13px root.)
const SpotlightTitle = styled.h4`
  font-family: var(--serif-stack);
  font-size: 1.85rem;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: var(--color-text-default);
  margin: 0;
`;

// UI (S) — 1.23rem ≈ 16px at the host's 13px root (sans-m equivalent).
// (Plain 1rem renders 13px on a 13px root.)
const SpotlightDescription = styled.p`
  font-family: var(--sans-stack);
  font-size: 1.23rem;
  line-height: 1.3;
  color: var(--color-text-default);
  margin: 0;
`;

interface SpotlightCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  active: boolean;
}

const SpotlightCard = ({ active, ...rest }: SpotlightCardProps) => (
  <SpotlightCardBase
    {...rest}
    aria-pressed={active}
    active={active}
  />
);

interface SpotlightsProps {
  activeSpotlightId: string | null;
  onSpotlightClick: (spotlight: Spotlight) => void;
  onClearSpotlight: () => void;
}

export function Spotlights({ activeSpotlightId, onSpotlightClick, onClearSpotlight }: SpotlightsProps) {
  const m = useT();
  // Clicking the active card clears it — replaces the separate "Filter
  // entfernen" pill that lived at the end of the row.
  const handleClick = (spotlight: Spotlight) => {
    if (activeSpotlightId === spotlight.id) {
      onClearSpotlight();
    } else {
      onSpotlightClick(spotlight);
    }
  };

  return (
    <Section>
      <SectionTitle>{m.spotlights.title}</SectionTitle>
      <ScrollRail>
        {SPOTLIGHTS.map(spotlight => (
          <SpotlightCard
            key={spotlight.id}
            active={activeSpotlightId === spotlight.id}
            onClick={() => handleClick(spotlight)}
          >
            <SpotlightTitle>{spotlightText(spotlight).title}</SpotlightTitle>
            <SpotlightDescription>{spotlightText(spotlight).description}</SpotlightDescription>
          </SpotlightCard>
        ))}
      </ScrollRail>
    </Section>
  );
}
